import type { VehicleId } from '../../types/gcs/vehicle';
import { calculateVehicleStatus } from '../../types/ui/vehicleStatus';
import type { VehicleStatus } from '../../types/ui/vehicleStatus';
import type { GcsData } from '../../types/gcs/gcsData';

/**
 * Selector to get the vehicle status for a specific vehicle
 * @param data - The GCS data map from the store
 * @param gcsId - The GCS ID
 * @param vehicleId - The vehicle ID
 * @returns The calculated vehicle status or null if data is not available
 */
export function selectVehicleStatus(
  data: Map<string, GcsData>,
  gcsId: string,
  vehicleId: VehicleId
): VehicleStatus | null {
  const gcsData = data.get(gcsId);
  if (!gcsData) return null;

  const launchpadId = gcsData.vehicleLaunchpadMap.get(vehicleId);
  if (!launchpadId) return null;
  const liveMission = gcsData.liveMissionsMap.get(vehicleId);
  const vehiclePreparationStatus = gcsData.vehiclePreparationStatusMap.get(vehicleId);
  const missionDetailedState = liveMission ? gcsData.missionDetailedStateMap.get(liveMission.missionId) : undefined;
  const launchpadPreflightMission = (() => {
    const notification = gcsData.launchpadPreflightMissionMap.get(launchpadId);
    return notification?.preflightInfo?.vehicleId === vehicleId &&
      notification?.preflightInfo?.missionId === liveMission?.missionId
      ? notification
      : undefined;
  })();

  const dockPreflightMission = (() => {
    const notification = liveMission?.dockPoint
      ? gcsData.dockPreflightMissionMap.get(liveMission.dockPoint.id)
      : undefined;
    return notification?.dockPreflightInfo?.vehicleId === vehicleId &&
      notification?.dockPreflightInfo?.missionId === liveMission?.missionId
      ? notification
      : undefined;
  })();

  return calculateVehicleStatus({
    missionStatus: missionDetailedState?.status,
    vehiclePreparationStatus,
    isReadyForTakeoffFromLaunchpad: launchpadPreflightMission?.preflightInfo?.started ?? false,
    isReadyForTakeoffFromDock: dockPreflightMission?.dockPreflightInfo?.isReadyForTakeoff ?? false
  });
}
